import React, { useEffect, useState } from "react";
import { Card, CardBody, Button } from "reactstrap";
import "./dashboard.scss";
import ReactApexChart from "react-apexcharts";
import { getRecords } from "../../services/api.service";
import { useNavigate } from "react-router-dom";

const InwardAndOutward = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2000 + 1 },
    (_, i) => 2000 + i
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [series, setSeries] = useState([
    { name: "inward", type: "column", data: Array(12).fill(0) },
    { name: "outward", type: "column", data: Array(12).fill(0) },
  ]);
  const [loading, setLoading] = useState(true);
  const [isFetch, setIsFetch] = useState(false);
  const [typeCostQTY, setTypeCostQTY] = useState(null);

  // Skeleton styles
  const skeletonStyle = {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite linear",
  };

  useEffect(() => {
    let timer;

    const fetchData = async () => {
      if (!isFetch) {
        if (selectedYear) {
          setLoading(false);
        } else {
          setLoading(true);
        }
        await fetchReports();
        setLoading(false);
      }
    };

    timer = setTimeout(fetchData, 500);

    const handleStorageChange = () => {
      fetchReports();
      setIsFetch(true);
    };

    window.addEventListener("dashboardUpdate", handleStorageChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("dashboardUpdate", handleStorageChange);
    };
  }, [selectedYear, isFetch]);

  const fetchReports = async () => {
    try {
      let dashboard_details = localStorage.getItem("dashboard_details");
      if (!dashboard_details) return;

      const payload = { ...JSON.parse(dashboard_details), year: selectedYear };
      setTypeCostQTY(payload.type);

      const response = await getRecords(
        payload,
        "/dashboard/getMonthlyInwardOutwardkCount"
      );

      if (response?.code === 1 && response.data) {
        const sanitizedData = response.data.map((series) => ({
          ...series,
          data: series?.data.map((value) => {
            const numericValue =
              typeof value === "string" ? parseFloat(value) : value;
            return numericValue === 0 ? null : numericValue;
          }),
        }));
        setSeries(sanitizedData);
      } else {
        console.error("Unexpected API response format", response);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value, 10));
    setIsFetch(false);
  };

  const options = {
    chart: { toolbar: { show: false } },
    stroke: { width: [0, 3], curve: "smooth" },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "25%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: { enabled: false },
    legend: {
      show: true,
      markers: {
        shape: "square",
        offsetX: -3,
        offsetY: -2,
      },
      itemMargin: { horizontal: 10, vertical: 0 },
    },
    colors: ["#5664d2", "#1cbb8c"],
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    xaxis: { tooltip: { enabled: false } },
    yaxis: {
      title: { text: typeCostQTY == "cost" ? "Cost" : "Quantity" },

      labels: {
        formatter: function (value) {
          if (value === null || value === undefined) return "";

          if (value >= 10000000) {
            return (value / 10000000).toFixed(1) + "Cr";
          } else if (value >= 100000) {
            return (value / 100000).toFixed(1) + "L";
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + "K";
          } else {
            return value.toString();
          }
        },
      },
    },
    tooltip: {
      y: {
        formatter: function (value) {
          return value?.toLocaleString("en-IN");
        },
      },
    },
  };

  return (
    <Card>
      <CardBody>
        <style>
          {`
            @keyframes shimmer {
              to { background-position: -200% 0; }
            }
          `}
        </style>

        <div className="float-end">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                width: "100px",
                height: "20px",
              }}
            />
          ) : (
            <select
              className="form-select form-select-sm"
              style={{ minHeight: "40px", overflowY: "auto" }}
              value={selectedYear}
              onChange={handleYearChange}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          )}
        </div>

        <h4 className="card-title mb-2">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                width: "200px",
                height: "24px",
              }}
            />
          ) : (
            "Inward and Outward"
          )}
        </h4>

        <div id="line-column-chart" className="apex-charts" dir="ltr">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                height: "300px",
                width: "100%",
              }}
            />
          ) : (
            <ReactApexChart
              options={options}
              series={series}
              type="line"
              height={300}
            />
          )}
        </div>

        <div className="d-flex justify-content-end mt-2">
          {loading ? (
            <div
              style={{
                ...skeletonStyle,
                width: "100px",
                height: "24px",
              }}
            />
          ) : (
            <Button
              color="link"
              className="d-flex align-items-center justify-content-center fw-bold p-0 text-decoration-none"
              onClick={() =>
                navigate("/inward-outward-wise-stocks", {
                  state: { year: selectedYear },
                })
              }
            >
              Details <i className="ri-arrow-right-line fs-5 ms-1"></i>
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default InwardAndOutward;
